import { useEffect, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import signUpWelcomeBonusRiv from '../../../../assets/auth/sign_up_welcome_bonus_v4.riv';
import { DialogLoader } from '../../../../components/Dialog/DialogLoader';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { I18Text } from '../../../../components/i18Next/I18Text';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { auth } from '../../../../services/auth';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';
import { EmailVerificationSuccessRedesign } from '../EmailVerificationSuccessRedesign/EmailVerificationSuccessRedesign';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './EmailVerificationRedesign.module.scss';
import envelope from './mail_with_crown_v2.png';

export const EmailVerificationRedesign = observer(function EmailVerificationRedesign({
  isCurrent,
  prevStep,
  nextStep,
}: WizardChildProps) {
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { authStore, portalStore, riveFileStore } = useStores();

  const { t } = useTranslation();

  const { sendVerificationEmailCooldown } = authStore;

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('resendEmailVerification')).get();

  const handleGoBack = async () => {
    const eventParams: AnalyticsEvents['email_account_check_email'] = {
      event_category: 'sign_up',
      event_action: 'click',
      click_location: 'close',
    };
    void analytics.sendEvent('email_account_check_email', eventParams);
    await auth.signOut();
    prevStep();
  };

  const handleClose = async () => {
    const eventParams: AnalyticsEvents['email_account_check_email'] = {
      event_category: 'sign_up',
      event_action: 'click',
      click_location: 'close',
    };
    void analytics.sendEvent('email_account_check_email', eventParams);
    await auth.signOut();
    portalStore.closePortal();
  };

  const getClosingProps = () => {
    if (showSuccessAnimation && isDesktop) {
      return {};
    }

    if (isDesktop) {
      return { onClose: handleClose };
    }
    return { onGoBack: handleGoBack };
  };

  const handleResend = async () => {
    try {
      if (sendVerificationEmailCooldown > 0) {
        return;
      }
      const eventParams: AnalyticsEvents['resend_email'] = {
        event_category: 'sign_up',
        event_action: 'click',
      };
      void analytics.sendEvent('resend_email', eventParams);
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  useEffect(() => {
    if (isCurrent) {
      const eventParams: AnalyticsEvents['email_account_check_email'] = {
        event_category: 'sign_up',
        event_action: 'impression',
      };
      void analytics.sendEvent('email_account_check_email', eventParams);

      void riveFileStore.addFileToPreload(signUpWelcomeBonusRiv);

      const unsubscribe = authStore.trackEmailVerificationCompletion(() => {
        setShowSuccessAnimation(true);
      });

      return () => unsubscribe();
    }
  }, [authStore, isCurrent, riveFileStore]);

  useEffect(() => {
    return () => authStore.clearSendVerificationEmailCooldown();
  }, [authStore]);

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="confirmation-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="confirmation-back-btn"
        title={t('emailVerification.title')}
        {...getClosingProps()}
      />

      <DialogLoader isProcessing={isProcessing} />

      <div className={styles.containerContent}>
        <div className={styles.topContainer}>
          <I18Text
            className={styles.regularText}
            components={{
              strong: <span className={styles.textGreenBold} />,
            }}
            i18nKey="emailVerification.emailVerifyWelcomeBonus"
            testid="email-verification-title"
          />

          <img alt="envelope" className={styles.envelopeImage} src={envelope} />

          <p className={styles.regularText}>
            {t('emailVerification.confirmationEmailSent')}{' '}
            <b className={styles.textEmail} data-testid="confirmation-user-mail">
              {authStore.currentUserEmail}
            </b>
          </p>
        </div>

        <p className={styles.emailArrivalHint}>{t('emailVerification.emailArrivalHint')}</p>

        <p className={styles.resendRow} data-testid="email-verification-redesign-resend-row">
          {isProcessing ? (
            <Trans
              components={{ resending: <span className={styles.resending} /> }}
              i18nKey="emailVerification.didntReceiveResendingSentence"
            />
          ) : (
            <Trans
              components={{
                resend: (
                  <span
                    className={classNames(styles.resendLink, {
                      [styles.resendLinkDisabled]: sendVerificationEmailCooldown > 0,
                    })}
                    data-testid="confirmation-resend-email-btn"
                    onClick={handleResend}
                  />
                ),
              }}
              i18nKey={
                sendVerificationEmailCooldown > 0
                  ? 'emailVerification.didntReceiveResendSecondsSentence'
                  : 'emailVerification.didntReceiveResendSentence'
              }
              values={{ seconds: sendVerificationEmailCooldown }}
            />
          )}
        </p>
        <ErrorMessage>{error}</ErrorMessage>
      </div>
      <div
        className={classNames(styles.successAnimationContainer, {
          [styles.successAnimationContainerHidden]: !showSuccessAnimation,
        })}
      >
        <EmailVerificationSuccessRedesign isCurrent={showSuccessAnimation} nextStep={nextStep} />
      </div>
    </RedesignContainer>
  );
});
